/**
 * `WireMockServer` — timer-driven fixture playback for offline client
 * development. Satisfies the `WireClient` interface so call sites are
 * agnostic to the underlying transport.
 *
 * Purpose: unblocks D4+ UI work before SERVER's Gateway (P1.5) lands.
 * Fixtures live at `client/src/__fixtures__/wire/` as `MockScenario`
 * records; `connect()` schedules the scripted stream on the next few
 * event-loop ticks.
 *
 * This is a skeleton. The full implementation (fixture loader, timer
 * playback, intent-echo support for DM-send round-trips) lands in the
 * same deliverable — kept minimal here so the interface compiles while
 * `envelopes.ts` + `client.ts` + `state.ts` stabilise.
 */

import type {
  ClientHandshake,
  ClientIntent,
  ServerMessage,
} from './envelopes';
import type {
  ConnectionEvent,
  ConnectionEventHandler,
  IncomingHandler,
  WireClient,
} from './client';

/**
 * A scripted message stream. `delay_ms_from_connect` drives when the server
 * message is emitted relative to the `connect()` call returning. Ordered by
 * delay; same-delay ties dispatch in array order.
 */
export interface MockScenarioStep {
  delay_ms_from_connect: number;
  message: ServerMessage;
}

export interface MockScenario {
  name: string;
  description: string;
  steps: MockScenarioStep[];
  /**
   * Optional echo handler. When the app sends a `ClientIntent`, the scenario
   * can respond with scripted `ServerMessage`s (e.g., DmSend → DmDelivery
   * echo). Runs synchronously after `send()` returns.
   */
  onIntent?: (intent: ClientIntent) => ServerMessage[];
}

export class WireMockServer implements WireClient {
  private readonly scenario: MockScenario;
  private readonly messageHandlers = new Set<IncomingHandler>();
  private readonly eventHandlers = new Set<ConnectionEventHandler>();
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private connected = false;

  constructor(scenario: MockScenario) {
    this.scenario = scenario;
  }

  async connect(_handshake: ClientHandshake): Promise<void> {
    if (this.connected) {
      throw new Error('WireMockServer: already connected');
    }
    this.connected = true;
    this.emitEvent({ kind: 'opened' });
    this.emitEvent({ kind: 'handshake_sent' });

    // Schedule scripted messages. HandshakeAck is expected at step 0.
    for (const step of this.scenario.steps) {
      const timer = setTimeout(() => {
        this.timers.delete(timer);
        this.dispatch(step.message);
      }, step.delay_ms_from_connect);
      this.timers.add(timer);
    }
  }

  send(intent: ClientIntent): void {
    if (!this.connected) {
      throw new Error('WireMockServer: send before connect');
    }
    const echoes = this.scenario.onIntent?.(intent);
    if (!echoes) return;
    for (const msg of echoes) {
      // Echo on the next tick so handlers see a clean stack.
      queueMicrotask(() => this.dispatch(msg));
    }
  }

  close(code = 1000, reason = 'client-initiated'): void {
    if (!this.connected) return;
    this.connected = false;
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
    this.emitEvent({ kind: 'closed', code, reason });
  }

  onMessage(handler: IncomingHandler): () => void {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  onEvent(handler: ConnectionEventHandler): () => void {
    this.eventHandlers.add(handler);
    return () => {
      this.eventHandlers.delete(handler);
    };
  }

  // ─── internal ────────────────────────────────────────────────────────

  private dispatch(msg: ServerMessage): void {
    if (!this.connected) return;
    // Emit a lifecycle event for the notable transitions; the real handler
    // stack derives connection stage transitions from these.
    if (msg.type === 'HandshakeAck') this.emitEvent({ kind: 'handshake_ack' });
    if (msg.type === 'Snapshot') this.emitEvent({ kind: 'snapshot_applied' });

    for (const h of this.messageHandlers) h(msg);
  }

  private emitEvent(ev: ConnectionEvent): void {
    for (const h of this.eventHandlers) h(ev);
  }
}
